import React from 'react';
import { FormControlLabel, Tooltip } from '@material-ui/core';
import { HelpOutline } from '@material-ui/icons';
import { TextField, PrimaryCheckbox, SecondaryButton } from '../../components/AtomComponents';
import type { ShipStationData } from './types';
import { StepCard, StepField, StepHeader, StepFormContainer } from './PxwStepComponents';

interface ShipStationStepProps {
  data: ShipStationData;
  onChange: (data: ShipStationData) => void;
  enabled: boolean;
  onToggleEnabled: (enabled: boolean) => void;
}

const ShipStationStep = ({ data, onChange, enabled, onToggleEnabled }: ShipStationStepProps) => {
  const set = (field: keyof ShipStationData) => (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ ...data, [field]: e.target.value.trim() });

  return (
    <StepCard>
      <StepHeader title="ShipStation" subtitle="Store, webhook &amp; API credentials" />
      <StepFormContainer>
        <div className="small-12 margin-bottom-3">
          <p className="pxw-field-label margin-bottom-1">Enable Integration</p>
          <FormControlLabel
            label="Enable ShipStation Integration"
            control={
              <PrimaryCheckbox
                name="enableShipStationIntegration"
                checked={enabled}
                onChange={(_e, checked) => onToggleEnabled(checked)}
              />
            }
          />
        </div>

        {enabled && (
          <>
            <div className="grid-x pxw-field-row">
              <div className="small-12 medium-6 pxw-col-left">
                <StepField label="Store Number">
                  <TextField size="small" fullWidth required name="storeNumber" placeholder="1234567" value={data.storeNumber} onChange={set('storeNumber')} />
                </StepField>
              </div>
              <div className="small-12 medium-6 pxw-col-right">
                <StepField
                  label={
                    <span>
                      Webhook URL
                      <Tooltip title="contact connectors team for webhook url" arrow placement="top" classes={{ popper: 'pxw-tooltip-popper' }}>
                        <HelpOutline className="pxw-info-icon" />
                      </Tooltip>
                    </span>
                  }
                >
                  <div className="pxw-input-action-row">
                    <TextField size="small" fullWidth required name="webhookUrl" placeholder="https://api.example.com/webhooks/shipstation" value={data.webhookUrl} onChange={set('webhookUrl')} />
                    {/* Note: Fetch button is currently disabled; a dedicated webhook fetch API will be integrated here in the future */}
                    <SecondaryButton type="button" disabled className="pxw-fetch-btn">
                      Fetch
                    </SecondaryButton>
                  </div>
                </StepField>
              </div>
            </div>

            <div className="grid-x pxw-field-row">
              <div className="small-12 medium-6 pxw-col-left">
                <StepField label="V1 API Key">
                  <TextField size="small" fullWidth required name="v1ApiKey" placeholder="pxw-v1-api-key" value={data.v1ApiKey} onChange={set('v1ApiKey')} />
                </StepField>
              </div>
              <div className="small-12 medium-6 pxw-col-right">
                <StepField label="V1 Secret Key">
                  <TextField size="small" fullWidth required name="v1SecretKey" placeholder="pxw-v1-secret-key" value={data.v1SecretKey} onChange={set('v1SecretKey')} />
                </StepField>
              </div>
            </div>

            <div className="grid-x pxw-field-row">
              <div className="small-12 medium-6 pxw-col-left">
                <StepField label="V2 Prod Key">
                  <TextField size="small" fullWidth required name="v2ProdKey" placeholder="pxw-v2-prod-key" value={data.v2ProdKey} onChange={set('v2ProdKey')} />
                </StepField>
              </div>
            </div>
          </>
        )}
      </StepFormContainer>
    </StepCard>
  );
};

export default ShipStationStep;
